import { useEffect, useMemo, useState } from "react";
import { Box, Flex, Skeleton, useComputedColorScheme } from "@mantine/core";
import test_entry_config from "../config/test_entry_config.json";
import VfaMapSvg from "../maps/VfaMapSvg";
import { AXIS_LABEL_WIDTH } from "../maps/ThresholdMap";
import {
  buildPatternGeometry,
  extentHeightDegrees,
  extentWidthDegrees,
  placeUnflipped,
  VfaMapExtent,
  VfaMapGeometry,
} from "../maps/vfaMapGeometry";
import {
  buildGrayScaleField,
  GrayScaleField,
  GrayScaleFieldConfig,
  GRAY_SCALE_TILE_BASE_PATH,
  INTERPOLATED_CELLS_PER_LOCATION,
  latticeValues,
  offLatticeGrayScaleTiles,
} from "./grayScaleField";
import { renderGrayScaleFieldImage } from "./grayScaleFieldImage";

interface GrayScaleMapProps {
  input: Array<string | number>;
  eye: string;
  testType: string;
  /**
   * Reserve the width the threshold map gives its axis degrees on either side,
   * so both maps come out the same size from the same column span — which is
   * what puts their horizontal axes on one line.
   */
  showAxisGutters?: boolean;
}

/**
 * The interpolated field reaches half an interpolated cell beyond the outermost
 * measured locations, which is short of the pattern's own cell edge. Both maps
 * still draw in the pattern's extent so that they share a scale and their
 * crosshairs land on the same line.
 */
function fieldExtent(geometry: VfaMapGeometry): VfaMapExtent {
  const { columnDegrees, rowDegrees, cellSpanDegrees } = geometry;
  const halfInterpolatedCell = cellSpanDegrees / INTERPOLATED_CELLS_PER_LOCATION / 2;

  return {
    leftDegrees: columnDegrees[0] - halfInterpolatedCell,
    rightDegrees: columnDegrees[columnDegrees.length - 1] + halfInterpolatedCell,
    topDegrees: rowDegrees[0] + halfInterpolatedCell,
    bottomDegrees: rowDegrees[rowDegrees.length - 1] - halfInterpolatedCell,
  };
}

export default function GrayScaleMap(props: GrayScaleMapProps) {
  const colorScheme = useComputedColorScheme("light");
  const [renderedField, setRenderedField] = useState<{
    field: GrayScaleField;
    url: string | null;
  } | null>(null);

  const patternConfig = props.testType
    ? test_entry_config.thresholdMap[props.testType]?.[props.eye]
    : undefined;
  // 24-2C shades its field from the 24-2 lattice it extends; the row layout has no room for its central points.
  const fieldTestType = props.testType
    ? (test_entry_config.thresholdMap[props.testType]?.grayScaleTestType ?? props.testType)
    : undefined;
  const fieldConfig = fieldTestType
    ? (test_entry_config.grayScaleMap[fieldTestType]?.[props.eye] as
        | GrayScaleFieldConfig
        | undefined)
    : undefined;

  const geometry = useMemo(
    () => (patternConfig ? buildPatternGeometry(patternConfig) : null),
    [patternConfig],
  );

  const field = useMemo(
    () =>
      fieldConfig && props.input?.length
        ? buildGrayScaleField(
            latticeValues(props.input, patternConfig?.offLatticeIndexes).map(Number),
            fieldConfig,
          )
        : null,
    [props.input, fieldConfig, patternConfig],
  );

  useEffect(() => {
    if (!field) return;
    let stale = false;
    renderGrayScaleFieldImage(field).then((url) => {
      if (!stale) setRenderedField({ field, url });
    });
    return () => {
      stale = true;
    };
  }, [field]);

  const offLatticeTiles = useMemo(
    () => offLatticeGrayScaleTiles(props.input ?? [], patternConfig?.offLatticeIndexes),
    [props.input, patternConfig],
  );

  const fieldImageUrl = renderedField?.field === field ? renderedField.url : null;
  const ready = geometry != null && field != null && renderedField?.field === field;

  return (
    <Skeleton visible={!ready} height={"100%"}>
      {ready && (
        <Flex align="center" style={colorScheme === "dark" ? { filter: "invert(1)" } : undefined}>
          {props.showAxisGutters && <Box w={AXIS_LABEL_WIDTH} style={{ flexShrink: 0 }} />}
          <Box flex={1} miw={0}>
            <VfaMapSvg
              geometry={geometry}
              crosshairColor="black"
              content="probabilityTile"
              probabilityTileBasePath={GRAY_SCALE_TILE_BASE_PATH}
              values={offLatticeTiles}
              underlay={
                fieldImageUrl ? (
                  <FieldImage href={fieldImageUrl} extent={fieldExtent(geometry)} />
                ) : undefined
              }
            />
          </Box>
          {props.showAxisGutters && <Box w={AXIS_LABEL_WIDTH} style={{ flexShrink: 0 }} />}
        </Flex>
      )}
    </Skeleton>
  );
}

function FieldImage({ href, extent }: { href: string; extent: VfaMapExtent }) {
  return (
    <image
      preserveAspectRatio="none"
      transform={placeUnflipped(0, 0)}
      x={extent.leftDegrees}
      y={-extent.topDegrees}
      width={extentWidthDegrees(extent)}
      height={extentHeightDegrees(extent)}
      href={href}
    />
  );
}
